import {
  type SocialConnectionRow,
  type TenancyMode,
} from "@nexys/user-management-client";
import { AlertCircle, Check, Copy, Pencil, Save, Trash2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import { Textarea } from "../ui/textarea.js";
import { Switch } from "../ui/switch.js";

export interface ProjectSettingsProps {
  projectId: string;
  /** Allow editing (project owner or superadmin). */
  canManage?: boolean;
  canDelete?: boolean;
  onDeleted?: () => void;
}

export const ProjectSettings = ({
  projectId,
  canManage = false,
  canDelete = false,
  onDeleted,
}: ProjectSettingsProps) => {
  const client = useUmClient();
  const { data, loading, error, refresh } = useCall("getProject", { projectId });
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const copyId = async () => {
    await navigator.clipboard.writeText(projectId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    const res = await client.deleteProject({ projectId });
    setDeleting(false);
    if (res.ok) {
      onDeleted?.();
    } else {
      setDeleteError(errorMessage(res.error, "Failed to delete product."));
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>
            {canManage ? "Update product information" : "Product information"}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {loading ? (
            <>
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-64" />
            </>
          ) : data ? (
            <>
              {canManage ? (
                <GeneralForm
                  projectId={projectId}
                  name={data.name}
                  slug={data.slug}
                  onSaved={refresh}
                />
              ) : (
                <>
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs">Name</Label>
                    <p className="text-sm font-medium">{data.name}</p>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-muted-foreground text-xs">Slug</Label>
                    <p className="font-mono text-sm">{data.slug}</p>
                  </div>
                </>
              )}
              <div className="grid gap-1">
                <Label className="text-muted-foreground text-xs">UUID</Label>
                <div className="flex items-center gap-2">
                  <code className="rounded bg-muted px-2 py-1 font-mono text-xs">
                    {projectId}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={copyId}
                    aria-label="Copy UUID"
                  >
                    {copied ? (
                      <Check className="size-3.5 text-green-600" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      {data ? (
        <Card>
          <CardHeader>
            <CardTitle>Tenancy &amp; access</CardTitle>
            <CardDescription>
              How tenants and sign-ups behave inside this product.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TenancyForm
              key={data.updatedAt}
              projectId={projectId}
              tenancyMode={data.tenancyMode}
              allowSignup={data.allowSignup}
              allowTenantCreation={data.allowTenantCreation}
              canManage={canManage}
              onSaved={refresh}
            />
          </CardContent>
        </Card>
      ) : null}

      {data ? (
        <Card>
          <CardHeader>
            <CardTitle>Localisation &amp; retention</CardTitle>
            <CardDescription>
              The locale users inherit when they set none of their own, and
              how long this product keeps its audit logs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LocaleRetentionForm
              key={data.updatedAt}
              projectId={projectId}
              defaultLocale={data.defaultLocale}
              logRetentionDays={data.logRetentionDays}
              canManage={canManage}
              onSaved={refresh}
            />
          </CardContent>
        </Card>
      ) : null}

      {data ? (
        <Card>
          <CardHeader>
            <CardTitle>Trusted origins</CardTitle>
            <CardDescription>
              Where this product&apos;s apps run. Requests naming this product
              from one of these origins can scope passkeys to it, receive the
              emailed sign-in link through it, land back on it and pass CORS,
              without touching the deployment&apos;s own TRUSTED_ORIGINS.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TrustedOriginsForm
              key={data.updatedAt}
              projectId={projectId}
              trustedOrigins={data.trustedOrigins}
              canManage={canManage}
              onSaved={refresh}
            />
          </CardContent>
        </Card>
      ) : null}

      {data ? (
        <Card>
          <CardHeader>
            <CardTitle>Single sign-on</CardTitle>
            <CardDescription>
              Social login providers for this product, each with its own
              OAuth client.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SocialConnections projectId={projectId} canManage={canManage} />
          </CardContent>
        </Card>
      ) : null}

      {canDelete && (
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Danger zone</CardTitle>
            <CardDescription>
              Deleting a product removes every tenant inside it (cascade).
              This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {deleteError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle />
                <AlertDescription>{deleteError}</AlertDescription>
              </Alert>
            )}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 /> Delete product
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete product?</DialogTitle>
                  <DialogDescription>
                    This will permanently delete{" "}
                    <strong>{data?.name ?? "this product"}</strong> and every
                    tenant inside it. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    loading={deleting}
                  >
                    Delete permanently
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const GeneralForm = ({
  projectId,
  name: initialName,
  slug: initialSlug,
  onSaved,
}: {
  projectId: string;
  name: string;
  slug: string;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [name, setName] = useState(initialName);
  const [slug, setSlug] = useState(initialSlug);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Server data refreshed underneath us (e.g. another tab saved) —
  // resync untouched fields.
  useEffect(() => setName(initialName), [initialName]);
  useEffect(() => setSlug(initialSlug), [initialSlug]);

  const dirty = name.trim() !== initialName || slug.trim() !== initialSlug;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const res = await client.updateProject({
      projectId,
      name: name.trim(),
      slug: slug.trim(),
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } else {
      setError(errorMessage(res.error, "Couldn't save product settings."));
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="project-name">
          <Input
            id="project-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Slug" htmlFor="project-slug">
          <Input
            id="project-slug"
            required
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="font-mono"
          />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <Button
          type="submit"
          loading={saving}
          disabled={!dirty || !name.trim() || !slug.trim()}
        >
          {saved ? <Check /> : <Save />}
          {saved ? "Saved" : "Save changes"}
        </Button>
      </div>
    </form>
  );
};

const TenancyForm = ({
  projectId,
  tenancyMode: initialMode,
  allowSignup: initialSignup,
  allowTenantCreation: initialTenantCreation,
  canManage,
  onSaved,
}: {
  projectId: string;
  tenancyMode: TenancyMode;
  allowSignup: boolean;
  allowTenantCreation: boolean;
  canManage: boolean;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [mode, setMode] = useState<TenancyMode>(initialMode);
  const [signup, setSignup] = useState(initialSignup);
  const [tenantCreation, setTenantCreation] = useState(initialTenantCreation);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    mode !== initialMode ||
    signup !== initialSignup ||
    tenantCreation !== initialTenantCreation;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const res = await client.updateProject({
      projectId,
      tenancyMode: mode,
      allowSignup: signup,
      allowTenantCreation: tenantCreation,
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } else {
      setError(errorMessage(res.error, "Couldn't save tenancy settings."));
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <Field
        label="Tenancy mode"
        htmlFor="project-tenancy-mode"
        description="Single keeps the product to exactly one tenant. Narrowing to single is refused while the product holds more than one tenant."
      >
        <Select
          value={mode}
          onValueChange={(v) => setMode(v as TenancyMode)}
          disabled={!canManage}
        >
          <SelectTrigger id="project-tenancy-mode" className="w-[12rem]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="multi">Multi-tenant</SelectItem>
            <SelectItem value="single">Single tenant</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="flex items-center justify-between gap-4">
        <div className="grid gap-0.5">
          <Label htmlFor="project-signup">Open sign-up</Label>
          <p className="text-muted-foreground text-xs">
            Allow self-service sign-up scoped to this product.
          </p>
        </div>
        <Switch
          id="project-signup"
          checked={signup}
          onCheckedChange={setSignup}
          disabled={!canManage}
        />
      </div>
      <div className="flex items-center justify-between gap-4">
        <div className="grid gap-0.5">
          <Label htmlFor="project-tenant-creation">Self-serve tenants</Label>
          <p className="text-muted-foreground text-xs">
            Allow any authenticated user to create a tenant inside this
            product.
          </p>
        </div>
        <Switch
          id="project-tenant-creation"
          checked={tenantCreation}
          onCheckedChange={setTenantCreation}
          disabled={!canManage}
        />
      </div>
      {canManage ? (
        <div className="flex items-center gap-3">
          <Button type="submit" loading={saving} disabled={!dirty}>
            {saved ? <Check /> : <Save />}
            {saved ? "Saved" : "Save changes"}
          </Button>
        </div>
      ) : null}
    </form>
  );
};

/// Product default locale (which email-template variant its users get)
/// plus how long its audit logs are kept.
const LocaleRetentionForm = ({
  projectId,
  defaultLocale: initialLocale,
  logRetentionDays: initialRetention,
  canManage,
  onSaved,
}: {
  projectId: string;
  defaultLocale: string;
  logRetentionDays: number;
  canManage: boolean;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [locale, setLocale] = useState(initialLocale);
  const [retention, setRetention] = useState(String(initialRetention));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retentionNum = Number(retention);
  const retentionValid =
    retention.trim() !== "" &&
    Number.isInteger(retentionNum) &&
    retentionNum >= 0 &&
    retentionNum <= 3650;
  const dirty =
    locale.trim().toLowerCase() !== initialLocale ||
    retentionNum !== initialRetention;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const res = await client.updateProject({
      projectId,
      defaultLocale: locale.trim().toLowerCase(),
      logRetentionDays: retentionNum,
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } else {
      setError(errorMessage(res.error, "Couldn't save these settings."));
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Default locale"
          htmlFor="project-locale"
          description="Language tag (e.g. en, fr-CA) for users who set none of their own."
        >
          <Input
            id="project-locale"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            disabled={!canManage}
            placeholder="en"
            className="font-mono"
          />
        </Field>
        <Field
          label="Log retention (days)"
          htmlFor="project-log-retention"
          description="How long audit logs for this product's tenants are kept. 0 keeps them forever."
        >
          <Input
            id="project-log-retention"
            type="number"
            min={0}
            max={3650}
            value={retention}
            onChange={(e) => setRetention(e.target.value)}
            disabled={!canManage}
            aria-invalid={!retentionValid}
          />
        </Field>
      </div>
      {canManage ? (
        <div className="flex items-center gap-3">
          <Button
            type="submit"
            loading={saving}
            disabled={!dirty || !locale.trim() || !retentionValid}
          >
            {saved ? <Check /> : <Save />}
            {saved ? "Saved" : "Save changes"}
          </Button>
        </div>
      ) : null}
    </form>
  );
};

/// The product's trusted origins, one per line.
const TrustedOriginsForm = ({
  projectId,
  trustedOrigins: initialOrigins,
  canManage,
  onSaved,
}: {
  projectId: string;
  trustedOrigins: string[];
  canManage: boolean;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [text, setText] = useState(initialOrigins.join("\n"));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const origins = parseUrlLines(text);
  const dirty = origins.join("\n") !== initialOrigins.join("\n");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    const res = await client.updateProject({ projectId, trustedOrigins: origins });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved();
    } else {
      setError(errorMessage(res.error, "Couldn't save the trusted origins."));
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <Field
        label="Origins"
        htmlFor="project-trusted-origins"
        description="One per line, scheme and host only (e.g. https://app.example.com, http://localhost:3000). Apps pass this product's id on sign-in and passkey calls for these to apply."
      >
        <Textarea
          id="project-trusted-origins"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="https://app.example.com"
          className="font-mono"
          disabled={!canManage}
        />
      </Field>
      {canManage ? (
        <div className="flex items-center gap-3">
          <Button type="submit" loading={saving} disabled={!dirty}>
            {saved ? <Check /> : <Save />}
            {saved ? "Saved" : "Save changes"}
          </Button>
        </div>
      ) : null}
    </form>
  );
};

/// One redirect URL per textarea line, trimmed, blanks dropped.
const parseUrlLines = (text: string): string[] =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

/// The redirect URL this deployment's own callback route answers on —
/// the value to register (and paste here) when the product runs
/// directly on this deployment. The console is served by the API, so
/// its origin is the server's. Proxied products (e.g. mounted at /um)
/// use their own full URL instead; either way the list mirrors the
/// provider console verbatim.
const suggestedRedirectUrl = (provider: string): string =>
  typeof window === "undefined" || !provider
    ? ""
    : `${window.location.origin}/api/auth/callback/${provider}`;

const REDIRECT_URLS_HELP =
  "The exact authorized redirect URIs registered with the provider — full URLs, one per line, copied verbatim from the provider console (e.g. https://app.example.com/um/api/auth/callback/google). Sign-ins send the entry matching the caller's origin, byte for byte; the first entry is the default, and users may only land back on these origins.";

/// Per-product social-login connection manager: list existing
/// connections, add one (provider dropdown from the catalog + client
/// id/secret/redirect URLs), edit, toggle enabled, delete. Secrets are
/// write-only.
const SocialConnections = ({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) => {
  const client = useUmClient();
  const { data, loading, error, refresh } = useCall("listSocialConnections", {
    projectId,
  });
  const catalog = useCall("getProviders");
  const [busyId, setBusyId] = useState<string | null>(null);

  const connections = data?.connections ?? [];

  const toggleEnabled = async (c: SocialConnectionRow) => {
    setBusyId(c.id);
    await client.updateSocialConnection({
      projectId,
      connectionId: c.id,
      enabled: !c.enabled,
    });
    setBusyId(null);
    refresh();
  };

  const remove = async (c: SocialConnectionRow) => {
    setBusyId(c.id);
    await client.deleteSocialConnection({ projectId, connectionId: c.id });
    setBusyId(null);
    refresh();
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-4">
      {loading ? (
        <Skeleton className="h-16 w-full" />
      ) : connections.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No social login providers configured for this product yet.
        </p>
      ) : (
        <div className="grid gap-2">
          {connections.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between gap-3 rounded-lg border bg-card p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium capitalize">{c.provider}</span>
                  <Badge variant={c.enabled ? "default" : "muted"}>
                    {c.enabled ? "enabled" : "disabled"}
                  </Badge>
                </div>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {c.clientId}
                </p>
                {c.redirectUrls.length > 0 ? (
                  <p className="truncate font-mono text-xs text-muted-foreground">
                    → {c.redirectUrls.join(", ")}
                  </p>
                ) : null}
              </div>
              {canManage ? (
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    checked={c.enabled}
                    onCheckedChange={() => void toggleEnabled(c)}
                    disabled={busyId === c.id}
                    aria-label="Toggle enabled"
                  />
                  <EditConnectionDialog
                    projectId={projectId}
                    connection={c}
                    onSaved={refresh}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => void remove(c)}
                    disabled={busyId === c.id}
                    aria-label="Delete connection"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {canManage ? (
        <AddConnectionDialog
          projectId={projectId}
          catalog={catalog.data?.providers ?? []}
          existing={connections.map((c) => c.provider)}
          onAdded={refresh}
        />
      ) : null}
    </div>
  );
};

const AddConnectionDialog = ({
  projectId,
  catalog,
  existing,
  onAdded,
}: {
  projectId: string;
  catalog: string[];
  existing: string[];
  onAdded: () => void;
}) => {
  const client = useUmClient();
  const available = catalog.filter((p) => !existing.includes(p));
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUrls, setRedirectUrls] = useState("");
  const [scopes, setScopes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setProvider("");
    setClientId("");
    setClientSecret("");
    setRedirectUrls("");
    setScopes("");
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.createSocialConnection({
      projectId,
      provider,
      clientId: clientId.trim(),
      clientSecret,
      redirectUrls: parseUrlLines(redirectUrls),
      scopes: scopes.trim() || null,
    });
    setLoading(false);
    if (res.ok) {
      onAdded();
      reset();
      setOpen(false);
    } else {
      setError(errorMessage(res.error, "Couldn't add the connection."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={available.length === 0}>
          Add social login
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a social login</DialogTitle>
          <DialogDescription>
            Pick a provider and paste this product's own OAuth client. The
            secret is stored encrypted and never shown again.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="Provider" htmlFor="conn-provider" required>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger id="conn-provider">
                <SelectValue placeholder="Choose a provider" />
              </SelectTrigger>
              <SelectContent>
                {available.map((p) => (
                  <SelectItem key={p} value={p} className="capitalize">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Client ID" htmlFor="conn-client-id" required>
            <Input
              id="conn-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field label="Client secret" htmlFor="conn-client-secret" required>
            <Input
              id="conn-client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field
            label="Redirect URLs"
            htmlFor="conn-redirect-urls"
            required
            description={REDIRECT_URLS_HELP}
          >
            <Textarea
              id="conn-redirect-urls"
              value={redirectUrls}
              onChange={(e) => setRedirectUrls(e.target.value)}
              rows={3}
              placeholder={
                suggestedRedirectUrl(provider) || "Choose a provider first"
              }
              className="font-mono"
            />
          </Field>
          <Field
            label="Scopes"
            htmlFor="conn-scopes"
            description="Optional space-separated override; leave blank for the provider defaults."
          >
            <Input
              id="conn-scopes"
              value={scopes}
              onChange={(e) => setScopes(e.target.value)}
              className="font-mono"
            />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={
                !provider ||
                !clientId.trim() ||
                !clientSecret ||
                parseUrlLines(redirectUrls).length === 0
              }
            >
              Add connection
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/// Edit an existing connection: client id, a replacement secret
/// (blank keeps the stored one), the redirect URL list and scopes.
const EditConnectionDialog = ({
  projectId,
  connection,
  onSaved,
}: {
  projectId: string;
  connection: SocialConnectionRow;
  onSaved: () => void;
}) => {
  const client = useUmClient();
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState(connection.clientId);
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUrls, setRedirectUrls] = useState(
    connection.redirectUrls.join("\n")
  );
  const [scopes, setScopes] = useState(connection.scopes ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setClientId(connection.clientId);
    setClientSecret("");
    setRedirectUrls(connection.redirectUrls.join("\n"));
    setScopes(connection.scopes ?? "");
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.updateSocialConnection({
      projectId,
      connectionId: connection.id,
      clientId: clientId.trim(),
      // Blank keeps the stored secret.
      ...(clientSecret ? { clientSecret } : {}),
      redirectUrls: parseUrlLines(redirectUrls),
      scopes: scopes.trim() || null,
    });
    setLoading(false);
    if (res.ok) {
      onSaved();
      setOpen(false);
    } else {
      setError(errorMessage(res.error, "Couldn't save the connection."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={`Edit ${connection.provider} connection`}
        >
          <Pencil className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">
            Edit {connection.provider} connection
          </DialogTitle>
          <DialogDescription>
            The client secret is write-only: leave it blank to keep the
            stored one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="Client ID" htmlFor="edit-conn-client-id" required>
            <Input
              id="edit-conn-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field label="Client secret" htmlFor="edit-conn-client-secret">
            <Input
              id="edit-conn-client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Unchanged"
              className="font-mono"
            />
          </Field>
          <Field
            label="Redirect URLs"
            htmlFor="edit-conn-redirect-urls"
            required
            description={REDIRECT_URLS_HELP}
          >
            <Textarea
              id="edit-conn-redirect-urls"
              value={redirectUrls}
              onChange={(e) => setRedirectUrls(e.target.value)}
              rows={3}
              placeholder={suggestedRedirectUrl(connection.provider)}
              className="font-mono"
            />
          </Field>
          <Field
            label="Scopes"
            htmlFor="edit-conn-scopes"
            description="Optional space-separated override; leave blank for the provider defaults."
          >
            <Input
              id="edit-conn-scopes"
              value={scopes}
              onChange={(e) => setScopes(e.target.value)}
              className="font-mono"
            />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={
                !clientId.trim() || parseUrlLines(redirectUrls).length === 0
              }
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
